import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { apiClient } from "@/lib/api";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const PENDING_SESSION_KEY = "vora_pending_google_session";

const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      let sessionId = null;

      // Phase 1: Check for pending session from a previous redirect
      const pending = sessionStorage.getItem(PENDING_SESSION_KEY);
      if (pending) {
        sessionId = pending;
        sessionStorage.removeItem(PENDING_SESSION_KEY);
      }

      // Phase 2: Extract from URL if no pending session
      if (!sessionId) {
        const hash = window.location.hash || "";
        const search = window.location.search || "";
        const fullUrl = window.location.href || "";

        if (hash.includes("session_id")) {
          const params = new URLSearchParams(hash.replace("#", ""));
          sessionId = params.get("session_id");
        }
        if (!sessionId && search.includes("session_id")) {
          const params = new URLSearchParams(search);
          sessionId = params.get("session_id");
        }
        if (!sessionId) {
          const match = fullUrl.match(/session_id=([^&#]+)/);
          if (match) sessionId = match[1];
        }

        // Store and reload to break cross-site redirect chain
        if (sessionId) {
          sessionStorage.setItem(PENDING_SESSION_KEY, sessionId);
          window.location.replace(window.location.origin + "/dashboard");
          return;
        }
      }

      if (!sessionId) {
        setError("Google sign-in did not return a session. Please try again.");
        return;
      }

      // Phase 3: Exchange session — uses apiClient (Authorization header, no cookies needed)
      try {
        const { data } = await apiClient.post("/auth/session", { session_id: sessionId });

        // Cookie is set by the backend response — just set user state
        login(data);

        if (data.onboarding_completed) {
          navigate("/dashboard", { replace: true });
        } else {
          navigate("/onboarding", { replace: true });
        }
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || "Unknown error";
        console.error("[AuthCallback] Failed:", detail);
        setError(`Sign-in failed: ${detail}`);
      }
    };

    processAuth();
  }, [navigate, login]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-transparent px-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-[15px] text-[#78716C]">{error}</p>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="px-5 py-2.5 rounded-xl border border-[#E5E2DD] bg-white text-[#57534E] text-[13px] font-semibold"
            >
              Back to Login
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl border-none bg-[#4E7A67] text-white text-[13px] font-semibold"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-transparent">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-12 h-12 text-[#4E7A67] animate-spin" />
        <p className="text-[#5F6368]">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
